import { pushStatus, pushSubscribe } from '@/shared/api/generated/notifications/notifications';
import { unwrap } from '@/shared/api/unwrap';
import { t } from '@/shared/i18n';

import { isIOS, useInstall } from './install';

/**
 * Web push: подписка этого браузера у его push-сервера и передача её
 * бэкенду. Ключ VAPID приходит от сервера (GET /me/push) — один на
 * окружение.
 */

export type PushSupport =
  /** Браузер умеет push и мы на защищённом origin. */
  | 'ok'
  /** iPhone/iPad: push работает только у установленного приложения. */
  | 'needs-install'
  /** Нет Notification/PushManager — старый браузер или встроенный WebView. */
  | 'unsupported';

export function pushSupport(): PushSupport {
  if (
    typeof window === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window)
  ) {
    return isIOS() && !useInstall.getState().standalone ? 'needs-install' : 'unsupported';
  }

  return 'ok';
}

/**
 * Готовый service worker. `navigator.serviceWorker.ready` не отвечает
 * никогда, если worker не зарегистрировался (ошибка в нём, приватный режим
 * Firefox) — ждём не дольше нескольких секунд, иначе профиль повис бы на
 * скелетоне.
 */
async function readyWorker(): Promise<ServiceWorkerRegistration | null> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, 8000);
    }),
  ]);
}

/** Текущая подписка этого браузера, если есть. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== 'ok') {
    return null;
  }

  const registration = await readyWorker();

  return registration === null ? null : registration.pushManager.getSubscription();
}

/** Ключ VAPID приходит base64url-строкой, а pushManager хочет байты. */
function decodeKey(key: string): Uint8Array<ArrayBuffer> {
  const padded = key + '='.repeat((4 - (key.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));

  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i);
  }

  return bytes;
}

/** Отдаёт подписку бэкенду. */
async function register(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();

  if (
    json.endpoint === undefined ||
    json.keys?.['p256dh'] === undefined ||
    json.keys['auth'] === undefined
  ) {
    throw new Error('Браузер вернул подписку без ключей');
  }

  await pushSubscribe({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys['p256dh'], auth: json.keys['auth'] },
  });
}

/**
 * Подписать этот браузер: разрешение → подписка у push-сервера → бэкенд.
 * Возвращает false, если человек запретил уведомления.
 *
 * Если в браузере уже есть подписка с другим ключом (ключи окружения
 * сменили), pushManager.subscribe падает — старая снимается и делается новая.
 */
export async function subscribePush(publicKey: string): Promise<boolean> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return false;
  }

  const registration = await readyWorker();
  if (registration === null) {
    throw new Error(t.profile.device.pushNoWorker);
  }

  const options: PushSubscriptionOptionsInit = {
    userVisibleOnly: true,
    applicationServerKey: decodeKey(publicKey),
  };

  let subscription: PushSubscription;

  try {
    subscription = await registration.pushManager.subscribe(options);
  } catch {
    await (await registration.pushManager.getSubscription())?.unsubscribe();
    subscription = await registration.pushManager.subscribe(options);
  }

  await register(subscription);

  return true;
}

/**
 * Держит подписку в согласии с сервером; вызывается после входа.
 *
 * Подписка живёт в браузере, а на сервере привязана к человеку: после
 * входа под другим аккаунтом в том же браузере endpoint надо передать
 * заново. Ключа не спрашиваем и разрешений не просим — только то, что
 * браузер уже разрешил.
 */
export async function syncPushSubscription(): Promise<void> {
  try {
    const subscription = await currentSubscription();
    if (subscription === null) {
      return;
    }

    const status = unwrap(await pushStatus({ endpoint: subscription.endpoint }));
    if (status.enabled && !status.subscribed) {
      await register(subscription);
    }
  } catch {
    // Не критично: без синхронизации push просто не придёт до следующего
    // захода в профиль.
  }
}

/**
 * Следит за сеансом: вошёл человек (или сменился) — подписка сверяется с
 * сервером. Ставится один раз на старте.
 */
export function watchSessionForPush(
  subscribe: (listener: () => void) => () => void,
  current: () => { status: string; user?: { id: string } },
): void {
  let lastUser: string | null = null;

  subscribe(() => {
    const session = current();
    const userID = session.status === 'authenticated' ? (session.user?.id ?? null) : null;

    if (userID !== null && userID !== lastUser) {
      void syncPushSubscription();
    }

    lastUser = userID;
  });
}
